import { Box, LoadingOverlay, Text } from "@mantine/core"
import { CalculatorIcon, PercentIcon, TrendUpIcon, TrophyIcon } from "@phosphor-icons/react"
import { useTranslation } from "react-i18next"
import { KpiCard } from "../cards/KpiCard"
import { SectionHeader } from "../cards/SectionHeader"
import classes from "../analytics.module.css"
import { MarginSummary } from "../types"
import { formatCompactMnt, formatDecimal, formatDelta, formatMnt, formatPct } from "../utils"

type Props = {
  data: MarginSummary | undefined
  isLoading: boolean
}

export function ProfitabilitySection({ data, isLoading }: Props) {
  const { t } = useTranslation(["dashboard"])

  const total = data?.data.totalMargin
  const pct   = data?.data.marginPct
  const avg   = data?.data.avgOrderMargin
  const top   = data?.data.topMarginProduct

  const totalFmt = total ? formatCompactMnt(total.value) : { display: "—", unit: "" }
  const avgFmt   = avg   ? formatCompactMnt(avg.value)   : { display: "—", unit: "" }
  const totalMom = formatPct(total?.momPct ?? null)
  const totalYoy = formatPct(total?.yoyPct ?? null)
  const pctDelta = formatDelta(pct?.momDelta ?? null)
  const avgMom   = formatPct(avg?.momPct ?? null)

  return (
    <Box pos="relative" className={classes.section}>
      <LoadingOverlay visible={isLoading} />
      <SectionHeader
        title={t("analytics.sections.profit.title", { defaultValue: "Ашигт ажиллагаа" })}
        helpKey="order.dashboard.section.analytics-profit"
        description={t("analytics.sections.profit.desc", { defaultValue: "Маржин дүн шинжилгээ" })}
      />

      <Box className={classes.kpiGrid}>
        <KpiCard
          icon={TrendUpIcon}
          helpKey="order.dashboard.field.analytics-total-margin"
          label={t("analytics.margin.totalMargin", { defaultValue: "Нийт маржин" })}
          value={totalFmt.display}
          unit={totalFmt.unit}
          changes={[
            { sign: totalMom.sign, text: totalMom.text, labelKey: "analytics.kpis.vsPrevMonth", label: "өмнөх сараас" },
            { sign: totalYoy.sign, text: totalYoy.text, labelKey: "analytics.kpis.vsPrevYear",  label: "өмнөх жилийн мөн үе" }
          ]}
        />
        <KpiCard
          icon={PercentIcon}
          helpKey="order.dashboard.field.analytics-margin-pct"
          label={t("analytics.margin.marginPct", { defaultValue: "Маржин хувь" })}
          value={pct ? formatDecimal(pct.value, 1) : "—"}
          unit="%"
          changes={[
            { sign: pctDelta.sign, text: pctDelta.sign === "flat" ? pctDelta.text : `${pctDelta.text}%`, labelKey: "analytics.kpis.vsPrevMonth", label: "өмнөх сараас" }
          ]}
        />
        <KpiCard
          icon={CalculatorIcon}
          helpKey="order.dashboard.field.analytics-avg-order-margin"
          label={t("analytics.margin.avgOrder", { defaultValue: "Дундаж ордер маржин" })}
          value={avgFmt.display}
          unit={avgFmt.unit}
          changes={[
            { sign: avgMom.sign, text: avgMom.text, labelKey: "analytics.kpis.vsPrevMonth", label: "өмнөх сараас" }
          ]}
        />
        <KpiCard
          icon={TrophyIcon}
          helpKey="order.dashboard.field.analytics-top-margin-product"
          label={t("analytics.margin.topProduct", { defaultValue: "Хамгийн өндөр маржинтай" })}
          value={top ? top.productName : "—"}
          textValue={!!top}
          changes={top ? [{ sign: "flat", text: `${top.marginPct.toFixed(0)}% · ${formatMnt(top.marginAmount)}`, label: "" }] : undefined}
        />
      </Box>

      {(data?.data.costUnknownCount ?? 0) > 0 ? (
        <Text className={classes.note}>
          {t("analytics.margin.costUnknown", { count: data?.data.costUnknownCount })}
        </Text>
      ) : null}

      {!isLoading && !data ? <Text className={classes.note} ta="center">{t("analytics.emptyState", { defaultValue: "Өгөгдөл байхгүй" })}</Text> : null}
    </Box>
  )
}
